import React, {useEffect, useCallback, useRef} from "react";
import Box from "@mui/material/Box";
import {Link, useNavigate} from "react-router-dom";
import {useAuth} from "hooks/useAuth";
import {APIGetActiveTests} from "features/tests/listing/api/tests";
import CustomDataTable from "shared/tables/CustomDataTable";
import moment from "moment";
import GenericButton from "shared/ui/GenericButton";
import { getRomanNumber } from "../../../../utils/functions";
import Typography from "@mui/material/Typography";
import CopyTextButton from "../../../../shared/ui/CopyTextButton";
import {APIPostDeleteTest} from "../../test/api/test";
import {TestActive} from "./_interfaces";
import {getActiveTableColumns} from "./_tableColumns";
import useDataTable from "../../../../hooks/useDataTable";
import { getTestBasePath, TEST_TYPES, TestType } from "../../../../shared/types/testTypes";

interface Props {
    type: TestType;
}

const TestsActive = ({type}: Props) => {
    const {update, user} = useAuth();
    const navigate = useNavigate();
    const testConfig = TEST_TYPES[type];

    const table = useDataTable("testsActive", "test", type, "");

    const {
        setLoading,
        testType,
        page,
        order,
        orderBy,
        submittedQuery,
        filterValues,
        currentFilters,
        setTableData,
        setTableColumns,
        setTotalRows,
        setPerPage,
    } = table;
    const pageRef = useRef(page);

    useEffect(() => {
        pageRef.current = page;
    }, [page]);

    const getActiveTests = (currentPage = pageRef.current) => {
        APIGetActiveTests({
            page: `?page=${currentPage + 1}`,
            sort: orderBy,
            order: order,
            search: submittedQuery,
            type: type,
            ...filterValues,
        })
            .then((response) => {
                if (response.data.success) {
                    // console.log("getActiveTests OK", response);
                    setTableData(response.data.data.data.map((test: any) => mapTestToRow(test)));
                    setTotalRows(response.data.data.total);
                    setPerPage(response.data.data.per_page);
                } else {
                    update("error", {show: true, code: "A002"});
                }
            })
            .catch(() => update("error", {show: true, code: "A001"}))
            .finally(() => setLoading(false));
    }

    const openTestRoute = useCallback((
        action: "previzualizare",
        testId: number | string,
    ) => {
        window.open(`teste/${action}/${testId}`);
    }, [testType]);

    const mapTestToRow = useCallback((test: TestActive) => {
        const isPrincipal = user.position === "principal";
        const isStudent = user.position === "student";
        const isTeacher = user.position === "teacher";

        const testStartsAt = moment(test.test_starts_at).tz("Europe/Bucharest");
        const testExpiresAt = moment(test.test_expires_at).tz("Europe/Bucharest");
        const currentDate = moment(new Date()).tz("Europe/Bucharest");

        const formatDate = (date: string) => moment(date).locale("ro").format("DD.MM.YYYY");
        const formatDateTime = (date: string) => moment(date).locale("ro").format("HH:mm, DD.MM.YYYY");

        const isTestOpen = testStartsAt.isAfter(currentDate)
            ? `Poate fi accesat la ${testStartsAt.locale("ro").format("DD.MM.YYYY, HH:mm")}`
            : test.test_is_open ? "Deschis" : "Nedeschis";

        const classroomLabel = test.classroom_grade
            ? `(${getRomanNumber(test.classroom_grade)}) ${test.classroom_name}`
            : test.classroom_name;

        const actions = (
            <Box display="flex" justifyContent="flex-end">
                {isStudent && (
                    <Box mr={1}>
                        <GenericButton
                            disabled={testStartsAt.isAfter(currentDate)}
                            type="start"
                            onClick={() => {
                                navigate(`/teste/${test.test}`);
                            }}
                        />
                    </Box>
                )}

                {(isTeacher || isPrincipal) && (
                    <Box mr={1}>
                        <GenericButton type="items" onClick={() => openTestRoute("previzualizare", test.test)}/>
                    </Box>
                )}

                {user.testShareLink && (isTeacher || isPrincipal) && (
                    <Box mr={1}>
                        <CopyTextButton disabled={!isTestOpen} studentId={test.user_id} testId={test.test} testExpireDate={testExpiresAt.locale("ro").format("YYYY-MM-DD HH:mm:ss")} />
                    </Box>
                )}

                {isTeacher && (
                    <Box>
                        <GenericButton
                            disabled={isTestOpen === "Deschis"}
                            type="delete"
                            onClick={() =>
                                confirmDelete(test)
                            }
                        />
                    </Box>
                )}
            </Box>
        );

        return {
            ...(testConfig.usesGrade
                ? {test_grade: test.test_grade}
                : {test_type: `${test.test_type} - ${test.test_grade}`}),
            test_created_at: formatDate(test.test_created_at),
            test_expires_at: formatDateTime(test.test_expires_at),
            test_time: test.test_time ? `${test.test_time} ${test.test_time === 1 ? "minut" : "minute"}` : ``,
            ...(!isStudent && {
                user_name: <Link to={`/elevi/${test.user_id}`}>{test.user_name}</Link>,
            }),
            classroom_name: isStudent
                ? classroomLabel
                : <Link to={`/clase/${test.classroom_id}`}>{classroomLabel}</Link>,
            test_is_open: isTestOpen,
            ...((isPrincipal || isStudent) && {
                owner_name: isPrincipal
                    ? <Link to={`/profesori/${test.owner_id}`}>{test.owner_name}</Link>
                    : test.owner_name,
            }),
            ...(!isStudent && {test_tag: test.test_tag}),
            test: test.test,
            actions,
        }
    }, [user.position, navigate, openTestRoute, user.testShareLink]);

    useEffect(() => {
        setLoading(true);
        getActiveTests();

    }, [page, orderBy, order, submittedQuery, currentFilters]);

    useEffect(() => {
        const columns = getActiveTableColumns(user.position, testType);
        setTableColumns(columns);
    }, [user.position, testType]);

    const deleteTest = (id: string, callback: Function) => {
        update("loading", {show: true, modal: true});
        APIPostDeleteTest({test: id})
            .then((response) => {
                if (response.data.success) {
                    // console.log("deleteClassroom OK", response);
                    getActiveTests(pageRef.current);
                } else {
                    if(response.data.code === 400) {
                        let error = response.data.data.value;
                        update("error", {show: true, message: error, closable: true, button: "Am inteles"});
                    } else {
                        update("error", {show: true, code: "A002"});
                    }
                }
                callback();
            })
            .catch(() => {update("error", { show: true, code: "A001" })})
            .finally(() => update("loading", {show: false}));
    };

    const confirmDelete = (test: { test: number; [key: string]: any }) => {
        update("confirm", {
            show: true,
            closable: true,
            icon: "fat trash-can",
            title: "Ești sigur că vrei să ștergi acest test?",
            message: (
                <>Testul <b>{test.test}</b> va fi sters.</>
            ),
            returnFunction: (callback: Function) => {
                deleteTest(test.test.toString(), callback);
            },
            buttonYes: "Șterge",
            buttonNo: "Închide",
        });
    };

    return (
        <>
            <Typography variant="body2" sx={{marginBottom: 1,}}>*Sesiunile de testare vor putea fi
                vizualizate mai jos în maximum 5 minute după generare. Dacă acestea nu apar în intervalul de timp
                precizat, vă rugăm să reîmprospătați pagina.
            </Typography>
            <CustomDataTable
                loading={table.loading}
                columns={table.tableColumns}
                rows={table.tableData}
                rowsPerPage={table.perPage}
                page={page}
                totalRows={table.totalRows}
                onPageChange={table.handleChangePage}
                handleRequestSort={table.handleRequestSort}
                orderBy={orderBy}
                order={order}
                hasSearch={true}
                onChange={table.handleSearchChange}
                onSearch={table.handleSearch}
                searchValue={table.query}
                onClearSearch={table.handleClearSearch}
            />
        </>
    );
};

export default TestsActive;
